import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface TenantInfo {
  name: string
  settings: {
    logoUrl?: string
    companyAddress?: string
    companyIco?: string
    companyDic?: string
    companyIcDph?: string
    companyIban?: string
    /** Skryté stĺpce tabuliek Reportov (spoločné pre kanceláriu). */
    reportHiddenColumns?: Partial<Record<'activity' | 'results', string[]>>
    newsletterEnabled?: boolean
    [key: string]: unknown
  }
}

export interface TenantSettings {
  name: string
  adminDomain: string
  settings: {
    companyPhone?: string
    companyEmail?: string
    companyAddress?: string
    companyIco?: string
    companyDic?: string
    companyIcDph?: string
    companyRegistrationOffice?: string
    companyRegistrationNumber?: string
    companyIban?: string
    companySwift?: string
    companyBankName?: string
    logoUrl?: string
    footerText?: string
    commissionBrokerRate?: number
    commissionBranchManagerRate?: number
    commissionCompanyRate?: number
    commissionSystemRate?: number
    tiperCanAssignBroker?: boolean
    propertyVisibility?: 'own' | 'branch' | 'tenant'
    contactVisibility?: 'shared' | 'separate'
    interestProtectionMonths?: number | null
  }
}

/**
 * Shared `GET /settings/tenant-info` query — tenant name + the public settings blob.
 * Keyed `['tenant-settings', 'info']`: the `/settings` mutations invalidate the
 * `['tenant-settings']` prefix, so a settings save refreshes every consumer (sidebar,
 * deal header, report columns). Must NOT share the exact key with the full
 * `GET /settings` query (`['tenant-settings', 'full']`) — the always-mounted sidebar
 * would overwrite it with this smaller shape (no domain, adminDomain…).
 */
export const TENANT_INFO_KEY = ['tenant-settings', 'info'] as const

export function useTenantInfo(options?: { enabled?: boolean }) {
  return useQuery<TenantInfo>({
    queryKey: TENANT_INFO_KEY,
    queryFn: async () => {
      const { data } = await api.get('/settings/tenant-info')
      return data
    },
    enabled: options?.enabled,
  })
}
